import {
    analyzeRepository,
    getPipeline,
    getStackStatus,
    listPipelines
} from './queries';
import {
    cleanupMigratedPipeline,
    createPipeline,
    deletePipeline,
    migrateCodePipeline,
    provisionPipeline,
    reprovisionPipeline,
    updatePipeline,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';

import {
    AnalyzeRepository,
    CleanupMigratedPipeline,
    CreatePipeline,
    DeletePipeline,
    GetPipeline,
    GetStackStatus,
    ListPipelines,
    MigrateCodePipeline,
    Pipeline,
    ProvisionPipeline,
    ProvisionPipelineInput,
    ReprovisionPipeline,
    UpdatePipeline
} from "../types";
import {pipelinesClient} from "../../pipelines-api-integration/client";


const client = pipelinesClient;

export const fetchRecords = async (query: {
    filter?: object;
    limit?: number;
    nextToken?: string | null;
}) => {
    const {filter, limit, nextToken} = query;
    const response = await client.graphql<GraphQLQuery<ListPipelines>>({
        query: listPipelines,
        variables: {filter, limit, nextToken},
    });
    return {
        items: response.data?.listPipelines.items ?? [],
        nextToken: response.data?.listPipelines.nextToken,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetPipeline>>({
                                                                         query: getPipeline,
                                                                         variables: {id: id},
                                                                     });
    return response.data?.getPipeline;
};

export const add = async (payload: object) => {
    const response = await client.graphql<GraphQLQuery<CreatePipeline>>({
                                                                            query: createPipeline,
                                                                            variables: {input: payload},
                                                                        });
    return response.data?.createPipeline;
};

// FNP-545: build the updatePipeline input by WHITELISTING only the fields that
// UpdatePipelineInput defines. The previous approach blacklisted known output
// fields off the loaded Pipeline record (delete record.created, etc.) and missed
// deploymentAccountGroupId — which exists on the Pipeline type and the
// Create/Provision inputs but NOT on UpdatePipelineInput — so AppSync rejected the
// whole mutation ("field not defined for input object type 'UpdatePipelineInput'").
// Whitelisting is the platform convention (build a fresh input from canonical
// fields only) and is immune to new Pipeline output fields appearing later.
export const buildUpdatePipelineInput = (record: Pipeline) => ({
    id: record.id,
    name: record.name,
    description: record.description,
    pipelineType: record.pipelineType,
    pipelineStatus: record.pipelineStatus,
    templateId: record.templateId,
    branchName: record.branchName,
    buildCommand: record.buildCommand,
    stages: record.stages?.map(s => ({
        name: s.name,
        order: s.order,
        buildProvider: s.buildProvider,
        deployProvider: s.deployProvider,
        requiresApproval: s.requiresApproval,
        stageKind: s.stageKind,
        region: s.region,
        // FNP-1267: these three shape what the stage actually runs and where
        // it writes. They were omitted from this whitelist, so editing them
        // would have been silently discarded on submit.
        buildCommand: s.buildCommand,
        artifactBaseDir: s.artifactBaseDir,
        stackName: s.stackName,
    })),
    deploymentAccounts: record.deploymentAccounts?.map(a => ({
        accountId: a.accountId,
        accountName: a.accountName,
        region: a.region,
        deploymentBucketName: a.deploymentBucketName,
        // FNP-1267: required by the builder, which no longer defaults it.
        deployRoleArn: a.deployRoleArn,
    })),
});

export const update = async (_id: string, record: Pipeline) => {
    const input = buildUpdatePipelineInput(record);

    const response = await client.graphql<GraphQLQuery<UpdatePipeline>>({
                                                                            query: (updatePipeline),
                                                                            variables: {input},
                                                                        });
    return response.data?.updatePipeline;
};

export const migrate = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<MigrateCodePipeline>>({
                                                                                  query: migrateCodePipeline,
                                                                                  variables: {input: {id: id}},
                                                                              });
    return response.data?.migrateCodePipeline;
};

export const cleanup = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<CleanupMigratedPipeline>>({
                                                                                      query: cleanupMigratedPipeline,
                                                                                      variables: {input: {id: id}},
                                                                                  });
    return response.data?.cleanupMigratedPipeline;
};

export const analyze = async (input: { repositoryName: string, branchName?: string }) => {
    const response = await client.graphql<GraphQLQuery<AnalyzeRepository>>({
                                                                                query: analyzeRepository,
                                                                                variables: {input: input},
                                                                            });
    return response.data?.analyzeRepository;
};

export const fetchStackStatus = async (stackName: string) => {
    const response = await client.graphql<GraphQLQuery<GetStackStatus>>({
                                                                            query: getStackStatus,
                                                                            variables: {stackName: stackName},
                                                                        });
    return response.data?.getStackStatus;
};

export const reprovision = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<ReprovisionPipeline>>({
                                                                                  query: reprovisionPipeline,
                                                                                  variables: {input: {id: id}},
                                                                              });
    return response.data?.reprovisionPipeline;
};

export const provision = async (payload: ProvisionPipelineInput) => {
    const response = await client.graphql<GraphQLQuery<ProvisionPipeline>>({
                                                                                query: provisionPipeline,
                                                                                variables: {input: payload},
                                                                            });
    return response.data?.provisionPipeline;
};

export const deleteRecord = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<DeletePipeline>>({
                                                                            query: (deletePipeline),
                                                                            variables: {input: {id: id}},
                                                                        });
    return response.data?.deletePipeline;
};
